import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { mapCommissionPlansResponseToDto } from '../../commission-plans/dto/commission-plans-list.dto.js';
import {
  mapPartnerDetailsResponseToDto,
  mapPartnerTreeResponseToDto,
  mapPartnerCommissionsResponseToDto,
  mapPartnerReferralsResponseToDto,
} from '../dto/partner-details.dto.js';
import {
  buildPartnerDetailsModel,
  buildPartnerTreeModel,
  buildPartnerCommissionsModel,
  buildPartnerReferralsModel,
} from '../model/partner-details.model.js';

export function usePartnerDetails() {
  const { token } = useAuth();
  const { uid } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [treeNodes, setTreeNodes] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editPlanUid, setEditPlanUid] = useState('');
  const [planOptions, setPlanOptions] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignProfileUid, setAssignProfileUid] = useState('');
  const [assignError, setAssignError] = useState('');
  const [showCommissionRecalculationModal, setShowCommissionRecalculationModal] = useState(false);
  const [commissionRecalculationError, setCommissionRecalculationError] = useState('');
  const [activeReferralTab, setActiveReferralTab] = useState('tree');
  const [directReferrals, setDirectReferrals] = useState([]);
  const [directReferralsTotal, setDirectReferralsTotal] = useState(0);
  const [directReferralsQuery, setDirectReferralsQuery] = useState('');
  const [directReferralsOffset, setDirectReferralsOffset] = useState(0);
  const [isDirectReferralsLoading, setIsDirectReferralsLoading] = useState(false);
  const directReferralsLimit = 20;
  const partnerRequestIdRef = useRef(0);
  const directReferralsRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const [commissionTypeFilter, setCommissionTypeFilter] = useState('');
  const [commissionStatusFilter, setCommissionStatusFilter] = useState('');

  const loadPartner = useCallback(async () => {
    const requestId = partnerRequestIdRef.current + 1;
    partnerRequestIdRef.current = requestId;

    if (!uid) {
      setPartner(null);
      setError('Partner uid is required');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const [partnerResponse, treeResponse, commissionsResponse, plansResponse] = await Promise.all([
        adminApi.getPartner(token, uid),
        adminApi.getPartnerTree(token, uid).catch(() => ({ nodes: [] })),
        adminApi.getPartnerCommissions(token, uid, {
          limit: 50,
          ...(commissionTypeFilter ? { type: commissionTypeFilter } : {}),
          ...(commissionStatusFilter ? { status: commissionStatusFilter } : {}),
        }).catch(() => ({ commissions: [] })),
        adminApi.getCommissionPlans(token).catch(() => ({ commissionPlans: [] })),
      ]);
      if (!isMountedRef.current || partnerRequestIdRef.current !== requestId) return;
      const dto = mapPartnerDetailsResponseToDto(partnerResponse);
      const model = buildPartnerDetailsModel(dto);
      setPartner(model);
      setEditStatus(dto?.status || '');
      setEditPlanUid(dto?.commissionPlanUid || '');
      setTreeNodes(buildPartnerTreeModel(mapPartnerTreeResponseToDto(treeResponse)));
      setCommissions(buildPartnerCommissionsModel(mapPartnerCommissionsResponseToDto(commissionsResponse)));
      const plans = mapCommissionPlansResponseToDto(plansResponse);
      setPlanOptions(plans.map((plan) => ({
        value: plan.uid,
        label: plan.name || plan.uid,
      })));
    } catch (requestError) {
      if (!isMountedRef.current || partnerRequestIdRef.current !== requestId) return;
      setPartner(null);
      setError(requestError.message || 'Failed to load partner details');
    } finally {
      if (!isMountedRef.current || partnerRequestIdRef.current !== requestId) return;
      setIsLoading(false);
    }
  }, [uid, token, commissionTypeFilter, commissionStatusFilter]);

  const loadDirectReferrals = useCallback(async ({
    query = directReferralsQuery,
    offset = directReferralsOffset,
  } = {}) => {
    if (!uid) return;

    const requestId = directReferralsRequestIdRef.current + 1;
    directReferralsRequestIdRef.current = requestId;

    setError('');
    setIsDirectReferralsLoading(true);
    try {
      const response = await adminApi.getPartnerReferrals(token, uid, {
        query,
        limit: directReferralsLimit,
        offset,
      });
      if (!isMountedRef.current || directReferralsRequestIdRef.current !== requestId) return;
      const model = buildPartnerReferralsModel(mapPartnerReferralsResponseToDto(response));
      setDirectReferrals(model.referrals);
      setDirectReferralsTotal(model.total);
    } catch (requestError) {
      if (!isMountedRef.current || directReferralsRequestIdRef.current !== requestId) return;
      setDirectReferrals([]);
      setDirectReferralsTotal(0);
      setError(requestError.message || 'Failed to load direct referrals');
    } finally {
      if (!isMountedRef.current || directReferralsRequestIdRef.current !== requestId) return;
      setIsDirectReferralsLoading(false);
    }
  }, [uid, token, directReferralsQuery, directReferralsOffset]);

  useEffect(() => {
    loadPartner();
  }, [loadPartner]);

  useEffect(() => () => {
    isMountedRef.current = false;
    partnerRequestIdRef.current += 1;
    directReferralsRequestIdRef.current += 1;
  }, []);

  useEffect(() => {
    if (activeReferralTab !== 'direct-referrals') return;
    loadDirectReferrals();
  }, [activeReferralTab, loadDirectReferrals]);

  const navigateBack = useCallback(() => { navigate('/partners'); }, [navigate]);

  const saveStatus = useCallback(async () => {
    if (!uid || !editStatus) return;
    setIsSaving(true); setError(''); setSuccess('');
    try {
      await adminApi.updatePartner(token, uid, { status: editStatus });
      setSuccess('Partner status updated');
      await loadPartner();
    } catch (requestError) {
      setError(requestError.message || 'Failed to update status');
    } finally { setIsSaving(false); }
  }, [uid, editStatus, token, loadPartner]);

  const savePlan = useCallback(async () => {
    if (!uid || !editPlanUid) return;
    setIsSaving(true); setError(''); setSuccess('');
    try {
      await adminApi.updatePartner(token, uid, { commissionPlanUid: editPlanUid });
      setSuccess('Commission plan updated');
      await loadPartner();
    } catch (requestError) {
      setError(requestError.message || 'Failed to update commission plan');
    } finally { setIsSaving(false); }
  }, [uid, editPlanUid, token, loadPartner]);

  const openAssignModal = useCallback(() => {
    setShowAssignModal(true); setAssignProfileUid(''); setAssignError('');
  }, []);

  const closeAssignModal = useCallback(() => {
    setShowAssignModal(false); setAssignProfileUid(''); setAssignError('');
  }, []);

  const openCommissionRecalculationModal = useCallback(() => {
    setShowCommissionRecalculationModal(true);
    setCommissionRecalculationError('');
  }, []);

  const closeCommissionRecalculationModal = useCallback(() => {
    setShowCommissionRecalculationModal(false);
    setCommissionRecalculationError('');
  }, []);

  const submitAssignClient = useCallback(async () => {
    if (!uid || !assignProfileUid.trim()) { setAssignError('Profile UID is required'); return; }
    setIsSaving(true); setAssignError('');
    try {
      await adminApi.assignClient(token, uid, { profileUid: assignProfileUid.trim() });
      setSuccess('Client assigned to partner');
      closeAssignModal();
      await loadPartner();
      if (activeReferralTab === 'direct-referrals') {
        setDirectReferralsOffset(0);
        await loadDirectReferrals({ query: directReferralsQuery, offset: 0 });
      }
    } catch (requestError) {
      setAssignError(requestError.message || 'Failed to assign client');
    } finally { setIsSaving(false); }
  }, [uid, assignProfileUid, token, loadPartner, closeAssignModal, activeReferralTab, directReferralsQuery, loadDirectReferrals]);

  const submitCommissionRecalculation = useCallback(async (payload) => {
    if (!uid) {
      setCommissionRecalculationError('Partner uid is required');
      return;
    }

    setIsSaving(true);
    setCommissionRecalculationError('');
    setSuccess('');
    try {
      await adminApi.recalculatePartnerCommissions(token, uid, payload);
      closeCommissionRecalculationModal();
      setSuccess('Partner commissions recalculation queued');
      await loadPartner();
    } catch (requestError) {
      setCommissionRecalculationError(requestError.message || 'Failed to queue partner commission recalculation');
    } finally {
      setIsSaving(false);
    }
  }, [uid, token, loadPartner, closeCommissionRecalculationModal]);

  return {
    partner, treeNodes, commissions, isLoading, isSaving, error, success,
    editStatus, setEditStatus, editPlanUid, setEditPlanUid, planOptions,
    showAssignModal, assignProfileUid, setAssignProfileUid, assignError,
    showCommissionRecalculationModal, commissionRecalculationError,
    activeReferralTab, setActiveReferralTab, directReferrals, directReferralsTotal,
    directReferralsQuery, setDirectReferralsQuery, directReferralsOffset, setDirectReferralsOffset,
    directReferralsLimit, isDirectReferralsLoading, loadDirectReferrals,
    commissionTypeFilter, setCommissionTypeFilter, commissionStatusFilter, setCommissionStatusFilter,
    navigateBack, saveStatus, savePlan, openAssignModal, closeAssignModal, submitAssignClient,
    openCommissionRecalculationModal, closeCommissionRecalculationModal, submitCommissionRecalculation,
  };
}
